import React, { ReactNode, useEffect, useState } from 'react';
import './TasksPage.css';
import { Status, TaskItem } from '../b2ai-tasks/models';
import TaskViewMenu from '../b2ai-tasks/TaskViewMenu';
import { useStorage } from '../../services/StorageProvider';
import {
  DemographicsDefinition,
  ConfoundersDefinition,
  GAD7Definition,
  PHQ9Definition,
  VHI10Definition,
  VoicePerceptionDefinition
} from '../b2ai-tasks/questionnaires/generic';
import {
  AudioCheckDefinition,
  DiadochokinesisDefinition,
  FreeSpeechDefinition,
  GlidesDefinition,
  HarvardSentencesDefinition,
  LoudnessDefinition,
  MaximumPhonationTimeDefinition,
  PictureDescriptionDefinition,
  ProlongedVowelDefinition,
  RespirationAndCoughDefinition,
  StoryRecallDefinition
} from '../b2ai-tasks/acoustic-tasks/generic';
import {
  CapeVSentencesDefinition,
  CaterpillarPassageDefinition
} from '../b2ai-tasks/acoustic-tasks/voice';
import {
  ADHDAdultDefinition,
  CustomAffectScaleDefinition,
  DSM5AdultDefinition,
  PANASDefinition
} from '../b2ai-tasks/questionnaires/mood';
import {
  ProductiveVocabularyDefinition,
  RandomItemGenerationDefinition,
  WordColorStroopDefinition
} from '../b2ai-tasks/acoustic-tasks/neuro';
import {
  DyspneaIndexDefinition,
  LeicesterCoughDefinition
} from '../b2ai-tasks/questionnaires/respiratory';
import { useApiService } from '../../services/ApiServiceProvider';
import { randomUUID } from '../utils';
import { useKeycloak } from '../../services/KeycloakProvider';
import { Skeleton, Typography } from '@mui/material';
import SnackbarAlert, { Severity } from '../alert/SnackbarAlert';
import { useLocalStorage } from '../../services/LocalStorageProvider';
import { GainProfile } from '../b2ai-tasks/acoustic-tasks/common/SubTaskView';
import { ProfileName } from '../b2ai-tasks/acoustic-tasks/common/models';
import { ApiParticipant, ApiSessionWithData, EligibleStudy } from '../../services/ApiService';
import { Questionnaire } from '../b2ai-tasks/questionnaires/common/models';
import { Session } from '../../models/Session';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const genericTasks: TaskItem[] = [
  {...AudioCheckDefinition,  status: 'pending'},
  {...DemographicsDefinition,  status: 'pending'},
  {...ConfoundersDefinition, status: 'pending'},
  {...VoicePerceptionDefinition, status: 'pending'},
  {...VHI10Definition, status: 'pending'},
  {...PHQ9Definition, status: 'pending'},
  {...GAD7Definition, status: 'pending'},
  {...ProlongedVowelDefinition, status: 'pending'},
  {...MaximumPhonationTimeDefinition, status: 'pending'},
  {...GlidesDefinition, status: 'pending'},
  {...HarvardSentencesDefinition, status: 'pending'},
  {...LoudnessDefinition, status: 'pending'},
  {...DiadochokinesisDefinition, status: 'pending'},
  {...FreeSpeechDefinition, status: 'pending'},
  {...RespirationAndCoughDefinition, status: 'pending'},
  {...PictureDescriptionDefinition, status: 'pending'},
  {...StoryRecallDefinition, status: 'pending'}
];

const moodTasks: TaskItem[] = [
  {...PANASDefinition, status: 'pending'},
  {...CustomAffectScaleDefinition, status: 'pending'},
  {...DSM5AdultDefinition, status: 'pending'},
  {...ADHDAdultDefinition, status: 'pending'}
];

const respiratoryTasks: TaskItem[] = [
  {...DyspneaIndexDefinition, status: 'pending'},
  {...LeicesterCoughDefinition, status: 'pending'}
];

const voiceTasks: TaskItem[] = [
  {...CapeVSentencesDefinition, status: 'pending'},
  {...CaterpillarPassageDefinition, status: 'pending'},
];

// TODO: comment out Neuro Tasks
const neuroTasks: TaskItem[] = [
  {...ProductiveVocabularyDefinition, status: 'pending'},
  {...RandomItemGenerationDefinition, status: 'pending'},
  {...WordColorStroopDefinition, status: 'pending'}
];

const allTasks: TaskItem[] = [
  ...genericTasks,
  ...moodTasks,
  ...respiratoryTasks,
  ...voiceTasks,
  ...neuroTasks
];

const createNewTasks = (participant: ApiParticipant) : TaskItem[] => {
  const tasks: TaskItem[] = [];

  // If control participant, assign only generic tasks
  if (participant.isControlParticipant) {
    return [...genericTasks];
  }

  if (participant.eligibleStudies) {
    const adultEligibleStudies= [
      EligibleStudy.VoiceDisorders,
      EligibleStudy.NeurologicalAndNeurodegenerativeDisorders,
      EligibleStudy.MoodAndPsychiatricDisorders,
      EligibleStudy.RespiratoryDisorders
    ];

    if (adultEligibleStudies.some(eligibleStudy => participant.eligibleStudies.includes(eligibleStudy))) {
      tasks.push(...genericTasks);
    }
    if (participant.eligibleStudies.includes(EligibleStudy.VoiceDisorders)) { tasks.push(...voiceTasks); }
    if (participant.eligibleStudies.includes(EligibleStudy.NeurologicalAndNeurodegenerativeDisorders)) { tasks.push(...neuroTasks); }
    if (participant.eligibleStudies.includes(EligibleStudy.MoodAndPsychiatricDisorders)) { tasks.push(...moodTasks); }
    if (participant.eligibleStudies.includes(EligibleStudy.RespiratoryDisorders)) { tasks.push(...respiratoryTasks); }
  }

  return [...new Set<TaskItem>(tasks)];
};

export const TasksPage = () => {
  const navigate = useNavigate();
  const { storage, updateStorage } = useStorage();
  const { storage: localStorage } = useLocalStorage();
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const apiService = useApiService();
  const { userInfo } = useKeycloak();
  const [participantId, setParticipantId] = useState<string | null>(null);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [snackbarContent, setSnackbarContent] = React.useState<ReactNode>();
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<Severity>('info');
  const { t } = useTranslation();

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (title: string, content: ReactNode, severity: Severity) => {
    setSnackbarTitle(title);
    setSnackbarContent(content);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    // Close snackbar after 5 seconds
    setTimeout(() => {
      handleSnackbarClose();
    }, 5000);
  };

  const recreateSessionTasks = (assignedTaskNames: string[], session: Session): TaskItem[] | never => {
    if (!assignedTaskNames || assignedTaskNames.length === 0) {
      throw new Error('No tasks assigned in this session. This can happen if a previous session was not completed. Please reach out to your Bridge2AI study contact for assistance.');
    }

    const tasks: TaskItem[] = [];
    for (const taskName of assignedTaskNames) {
      const task = allTasks.find(t => t.name === taskName);
      if (task) {
        tasks.push({ ...task, status: session ? getStatusOfTask(task, session) : 'pending' });
      } else {
        throw new Error(`Task not found: ${taskName}`);
      }
    }

    // If no gain profiles, set Audio Check to pending
    const gainProfiles: GainProfile[] = localStorage.gainProfiles ?? [];
    const requiredGainProfiles: ProfileName[] = ['speech', 'cough', 'breathe'];
    const requiredGainProfilesExist = requiredGainProfiles.every(name => gainProfiles.some(profile => profile.name === name)
    );

    console.log('Required gain profiles exist:', requiredGainProfilesExist);
    let audioCheckTask: TaskItem | undefined = tasks.find(task => task.name === AudioCheckDefinition.name);
    if (!requiredGainProfilesExist || audioCheckTask === undefined) {
      if (tasks.filter(task => task.status === 'pending' && task.type === 'acoustic-task').length > 0) {
        if (!audioCheckTask) {
          audioCheckTask = { ...AudioCheckDefinition, status: 'pending' };
          tasks.unshift(audioCheckTask);
        }

        if (audioCheckTask.status !== 'pending') {
          audioCheckTask.status = 'pending';
        }
      }
    }
    return tasks;
  };

  const createNewSession = async (participant: ApiParticipant, tasks: TaskItem[]) : Promise<ApiSessionWithData> => {
    const session = {
      id: randomUUID(),
      subjectId: participant.id,
      startedAt:  new Date().toISOString(),
      completedAt: '',
      status: 'inProgress',
      acousticTasks: [],
      completedBy: [],
      site: participant.site,
      isControlParticipant: participant.isControlParticipant,
      assignedTasks: tasks.map(t => t.name)
    };

    await apiService.createSession(session);
    return session;
  };

  const getParticipantDetails = async (participantId: string) => {
    return await apiService.getParticipantDetails(participantId);
  }

  const getStatusOfTask = (task: TaskItem, session: any): Status => {
    if (task.type === 'acoustic-task') {
      const taskFound = session?.acousticTasks.find((t: any) => t.name === task.name && t.cohort === task.cohort);
      if (taskFound) {
        return taskFound.status as Status;
      }
    } else if (task.type === 'questionnaire') {
      const questionnaire = task as Questionnaire;
      const taskFound = session[`${questionnaire.key}`];
      if (taskFound) {
        return taskFound.completedAt ? 'completed' : 'pending';
      }
    }

    return 'pending';
  }

  const initializeSession = async (participantId: string) => {
    try {
      const participant = await getParticipantDetails(participantId);
      console.log('Participant details:', participant);
      let session = participant.sessionWithData;

      // Check if session is null (no session) or 2 = completed
      if (!session || session.status === '2') {
        console.log('Creating new session');
        const tasks = createNewTasks(participant);
        if (!tasks || tasks.length === 0) {
          navigate('/error', { state: { message: 'No tasks available for you right now. Please reach out to your Bridge2AI study contact for assistance.' } });
          return;
        }

        session = await createNewSession(participant, tasks);

        // NOTE: There is not subjectId. A new session is created.
        // session.subjectId is used by the payload in "QuestionnaireTaskView.tsx"
        session.subjectId = participantId;
      } else {
        console.log('Session already exists:', session.id);
      }

      updateStorage({ session, participant });
    }
    catch (error: any)  {
      console.error('Error initializing session:', error);
      showSnackbar(
        'Could not create session',
        <Typography variant="body1">
          {error?.message || 'Network error: Check your internet connection and try again'}
        </Typography>,
        'error'
      );
    }
  };

  useEffect(() => {
    if (!storage.participant || !storage.session) { return; }

    if (storage.tasks && storage.tasks.length > 0) {
      setTasks(storage.tasks);
    } else {
      // Retrieve tasks based on assigned tasks in session
      const assignedTaskNames: string[] = storage.session.assignedTasks || [];
      let tasks: TaskItem[] = [];
      try {
        tasks = recreateSessionTasks(assignedTaskNames, storage.session);

      } catch (error) {
        console.error('Error creating tasks for this session:', error);
        navigate('/error', { state: { message: `Error creating tasks for this session. ${(error as Error).message}` } });
        return;
      }
      setTasks(tasks);
    }
  }, [storage.session, storage.participant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!participantId) { return; }

    console.log('Fetching session');

    initializeSession(participantId);

  }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInfo && userInfo.subjectId && userInfo.subjectId !== participantId) {
      setParticipantId(userInfo.subjectId);
    }
  }, [userInfo, participantId]);

  useEffect(() => {
    if (storage.tasks !== tasks) {
      updateStorage({ tasks });
    }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tasks-page">
      {tasks.length > 0
        ? <TaskViewMenu tasks={tasks} onChange={setTasks} />
        : [0,1,2,3].map((i, index) => (
            <div key={index} >
              <Skeleton variant="rectangular"
                width="100%"
                height={72}
                style={{borderRadius: '1rem', marginBottom: '1rem'}} aria-label={t('Loading task placeholder')} />
            </div>
          ))
      }
      <SnackbarAlert
        open={snackbarOpen}
        title={snackbarTitle}
        severity={snackbarSeverity}
        content={snackbarContent}
        vertical='top'
        onClose={handleSnackbarClose}
      />
    </div>
  );
};
